import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useDecideEconRunMutation,
  useDeleteEconRunMutation,
  useGetEconRunQuery,
  useLazyExportEconXlsxQuery,
} from '@shared/api';
import { Card } from '@shared/ui/Card';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Progress } from '@shared/ui/Progress';
import { ConfirmDialog } from '@shared/ui/ConfirmDialog';
import {
  IconArrowLeft,
  IconDownload,
  IconTrash,
} from '@shared/ui/icons';
import {
  apiErrorMessage,
  completenessExplanation,
  completenessLabel,
  completenessTone,
  extractedKsPeriod,
  fmtExtractedPeriod,
  fmtPeriod,
  fmtRub,
  isEconModuleId,
  isRunActive,
  ECON_MODULES,
  deviationComments,
  labelMetric,
  periodMismatch,
  reportError,
  revenueLikelyIncomplete,
  runCompleteness,
  runStageLabel,
  statusLabel,
  statusTone,
  unmappedRevenueContracts,
  useEconPermission,
} from '@entities/economic/types';
import { actionRefusal, useActionPermission } from '@entities/user';
import { AnomaliesPanel } from '../AnomaliesPanel';
import { RunChatPanel } from '../RunChatPanel';
import { CashCalendar } from '../CashCalendar';
import { ExecPackCard } from '../ExecPackCard';
import { DeviationComments } from '../DeviationComments';
import {
  ActionGroup,
  ActionHint,
  Back,
  CompletenessBox,
  CompletenessExplain,
  CompletenessHead,
  CompletenessTitle,
  ErrBox,
  HeadActions,
  HeadLeft,
  HeadRow,
  MismatchNote,
  Muted,
  NO_DECISION_RIGHT,
  OWN_RUN_HINT,
  PeriodCheck,
  PeriodLabel,
  PeriodTitle,
  PeriodValue,
  ProgressBlock,
  ProgressPercent,
  ProgressStage,
  ProgressTopRow,
  ReportErrorBox,
  ReportErrorHint,
  ReportErrorReason,
  ReportErrorTitle,
  ReviewBar,
  ReviewText,
  SectionTitle,
  SubMeta,
  UnmappedBox,
  UnmappedContract,
  UnmappedHint,
  UnmappedItem,
  UnmappedLead,
  UnmappedList,
  UnmappedMetric,
  UnmappedRevenue,
  UnmappedTitle,
  Wrap,
} from './styles';
import { FactsResult } from './FactsResult';
import { SourcesSection } from './SourcesSection';

export function RunDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can_write } = useEconPermission();
  const approval = useActionPermission('econ.bdr.approve');

  const [poll, setPoll] = useState(0);
  const { data, isLoading, isError } = useGetEconRunQuery(id ?? '', {
    skip: !id,
    pollingInterval: poll,
  });

  const [decide, { isLoading: deciding }] = useDecideEconRunMutation();
  const [deleteRun, { isLoading: deleting }] = useDeleteEconRunMutation();
  const [exportXlsx, { isFetching: exporting }] = useLazyExportEconXlsxQuery();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const run = data?.run;

  // Статус/прогресс обработки приходят в реальном времени по WS-событию
  // `econ_run_updated` (rtkBridge инвалидирует тег { EconRun, id } → RTK
  // перетягивает карточку). Поэтому частый poll больше не нужен. Оставляем
  // редкий страховочный рефетч (60с) ТОЛЬКО пока обработка активна — на случай
  // потери WS-соединения (reconnect с backoff в ws.ts может занять до 30с,
  // плюс пропущенные за время разрыва события не догоняются). На терминальном
  // статусе poll выключаем полностью.
  useEffect(() => {
    setPoll(run && isRunActive(run.status) ? 60000 : 0);
  }, [run]);

  if (isLoading) {
    return (
      <Card>
        <Muted>Загрузка обработки…</Muted>
      </Card>
    );
  }
  if (isError || !data || !run) {
    return (
      <Card>
        <Muted>Не удалось загрузить обработку. Возможно, она была удалена.</Muted>
      </Card>
    );
  }

  const moduleShort = isEconModuleId(run.module) ? ECON_MODULES[run.module].short : run.module;
  const decided = Boolean(run.decision);
  const canDownload = run.status === 'completed';
  // Бизнес-процесс отчёта: завершён → экономист утверждает (decide accepted →
  // backend ставит сборку XLSX в очередь) → отчёт можно скачать. Скачивание до
  // утверждения гарантированно даёт 409 «отчёт ещё не сформирован» — поэтому
  // кнопку «Скачать» гасим до утверждения с явной подсказкой, а не ловим ошибку
  // постфактум. (Признака «отчёт готов» в карточке нет — гейтим по предусловию
  // «утверждён»; если сборка ещё идёт/упала, покажем backend-сообщение 409.)
  const reportApproved = run.decision === 'accepted';
  const downloadDisabled = exporting || !reportApproved;
  const downloadHint = reportApproved
    ? undefined
    : 'Сначала утвердите обработку — отчёт сформируется автоматически';
  // Полнота обработки (презентационный гейт). Единый источник истины для
  // бейджа статуса, пометки итогового дохода и блокировки кнопки «Утвердить».
  // Зеркалит backend-гейт (409 при accepted): blocking = неразобранные critical
  // ИЛИ summary.has_blocking_anomaly ИЛИ summary.failed_ks_sources > 0.
  const completeness = runCompleteness(run.summary, data.anomalies);
  // Что можно с обработкой, решает сервер (`access`, ТЗ Э0б, Д12): список общий
  // для отдела, решение — право записи (Д8 добавит право-действие), удалить —
  // автор или администратор и только неутверждённую. Без `access` (старый
  // backend) — прежнее правило «право записи».
  const access = data.access;
  const canDecide = access ? access.can_decide : can_write;
  const decideReason = access?.decide_blocked_reason ?? (can_write ? null : 'Только просмотр');
  const canDelete = access ? access.can_delete : can_write;
  const deleteReason = access?.delete_blocked_reason ?? (can_write ? null : 'Только просмотр');
  // Утверждение запрещено при блокирующей неполноте — кнопку гасим с причиной
  // ДО клика, чтобы пользователь не утвердил неполный отчёт и не ловил 409.
  const approveBlocked = completeness.level === 'blocking';
  // Решать (принять, отклонить, оспорить) — по `access` сервера: право записи
  // и право-действие `econ.bdr.approve` (Д8). Утвердить свою обработку —
  // только если роль разрешает своё («автор ≠ утверждающий»).
  const decideRefusal = actionRefusal(approval);
  const acceptRefusal = actionRefusal(approval, run.user_id);
  const decideHint = !canDecide
    ? (decideReason ?? 'Только просмотр')
    : decideRefusal
      ? NO_DECISION_RIGHT
      : undefined;
  const approveDisabled =
    decideHint !== undefined || acceptRefusal !== null || deciding || approveBlocked;
  const approveHint =
    decideHint ??
    (acceptRefusal === 'own'
      ? OWN_RUN_HINT
      : approveBlocked
        ? 'Разберите критические аномалии (в т.ч. нераспознанные источники) — утверждение неполного БДР запрещено'
        : undefined);
  // Период, извлечённый из документов (структурный из summary, если backend
  // его прокидывает, иначе LLM-эвристика payload.period строк). Сверяем с
  // выбранным периодом обработки и подсвечиваем расхождение.
  const ksPeriod = extractedKsPeriod(run.summary, data.rows);
  const showPeriodMismatch = periodMismatch(run.period, ksPeriod);
  // Причина провала фоновой сборки отчёта XLSX (после «Утвердить»). Backend
  // кладёт текст в run.summary.report_error и очищает при успешной пересборке;
  // непуст → показываем заметный баннер. Кнопку «Утвердить» НЕ блокируем —
  // повторное утверждение = пересборка отчёта.
  const reportBuildError = reportError(run.summary);
  // Договоры с выручкой, не привязанные к карте активного шаблона (R3): расчёт
  // верен, но в собранном XLSX по ним выйдет 0. Backend кладёт перечень в
  // run.summary.unmapped_revenue_contracts; непуст → показываем warning-баннер
  // с подсказкой «добавьте строки этих договоров в шаблон и пересоберите».
  const unmappedRevenue = unmappedRevenueContracts(run.summary);
  // Комментарии к существенным отклонениям план/факт (Р-13): деловые LLM-
  // пояснения, сгенерированные backend'ом. Показываем отдельной секцией —
  // раньше текст сохранялся, но нигде не выводился.
  const deviationCommentList = deviationComments(run.summary);
  // Стабильная ссылка на модуль для замыканий-обработчиков: внутри async-функций
  // TS не сохраняет narrowing `run` (захват возможно-undefined), а string-const
  // захватывается безопасно.
  const runModule = run.module;

  async function handleDecision(decision: 'accepted' | 'rejected') {
    if (!id) return;
    setActionError(null);
    try {
      await decide({ id, decision }).unwrap();
    } catch (err) {
      // 409 (регуляторный гейт: критические аномалии без разбора) несёт
      // объяснение в error.message — показываем его, а не общую фразу.
      setActionError(apiErrorMessage(err) ?? 'Не удалось сохранить решение. Попробуйте ещё раз.');
      // eslint-disable-next-line no-console
      console.error('decideEconRun failed', err);
    }
  }

  async function handleExport() {
    if (!id) return;
    setActionError(null);
    try {
      const blob = await exportXlsx(id).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `econ_report_${id}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      // 409 «отчёт ещё не сформирован» / «у модуля нет активного шаблона…» —
      // backend объясняет причину внятно по-русски, показываем как есть, а не
      // глотаем (раньше UI проглатывал 409, пользователь не понимал, что не так).
      setActionError(apiErrorMessage(err) ?? 'Не удалось выгрузить отчёт XLSX.');
      // eslint-disable-next-line no-console
      console.error('exportEconXlsx failed', err);
    }
  }

  async function handleDelete() {
    if (!id) return;
    try {
      await deleteRun(id).unwrap();
      navigate(`/agents/econ/runs?module=${runModule}`);
    } catch (err) {
      // 409 «утверждённую обработку удалить нельзя» объясняет причину —
      // показываем её. 403 (не автор) кнопка не допускает заранее.
      setConfirmOpen(false);
      setActionError(apiErrorMessage(err) ?? 'Не удалось удалить обработку.');
      // eslint-disable-next-line no-console
      console.error('deleteEconRun failed', err);
    }
  }

  return (
    <Wrap>
      <Back to={`/agents/econ/runs?module=${run.module}`}>
        <IconArrowLeft size={16} />
        <span>К списку обработок</span>
      </Back>

      <Card>
        <HeadRow>
          <HeadLeft>
            <PeriodTitle>{fmtPeriod(run.period)}</PeriodTitle>
            <SubMeta>
              <Badge $tone="neutral">{moduleShort}</Badge>
              {run.status === 'completed' && completeness.level !== 'complete' ? (
                // Внешний аудит: при неполноте НЕ показываем «БДР готово» —
                // экономист не должен принять огрызок дохода за полный отчёт.
                <Badge $tone={completenessTone(completeness.level)}>
                  {completenessLabel(completeness.level)}
                </Badge>
              ) : (
                <Badge $tone={statusTone(run.status)}>{statusLabel(run.status)}</Badge>
              )}
              {run.object_scope && <span>{run.object_scope}</span>}
              <span>автор: {data.author_name ?? 'система'}</span>
              <span>
                источников: {data.sources.length} · показателей: {data.facts.length} · аномалий:{' '}
                {data.anomalies.length}
              </span>
            </SubMeta>
          </HeadLeft>
          <HeadActions>
            {canDownload && (
              <ActionGroup>
                <Button
                  type="button"
                  $variant="secondary"
                  $size="sm"
                  onClick={handleExport}
                  disabled={downloadDisabled}
                  title={downloadHint}
                  aria-describedby={downloadHint ? 'download-hint' : undefined}
                >
                  <IconDownload size={15} />
                  {exporting ? 'Готовлю…' : 'Скачать XLSX'}
                </Button>
                {downloadHint && <ActionHint id="download-hint">{downloadHint}</ActionHint>}
              </ActionGroup>
            )}
            <ActionGroup>
              <Button
                type="button"
                $variant="ghost"
                $size="sm"
                onClick={() => setConfirmOpen(true)}
                disabled={!canDelete || deleting}
                title={deleteReason ?? undefined}
                aria-describedby={deleteReason ? 'delete-hint' : undefined}
              >
                <IconTrash size={15} />
                Удалить
              </Button>
              {deleteReason && <ActionHint id="delete-hint">{deleteReason}</ActionHint>}
            </ActionGroup>
          </HeadActions>
        </HeadRow>

        {run.status === 'completed' && completeness.level !== 'complete' && (
          // Заметное пояснение неполноты под шапкой: что именно не вошло в итог
          // и можно ли утверждать. blocking → danger + role=alert (важно прервать
          // чтение экрана), gaps → warning + role=status (предупреждение, не ошибка).
          <CompletenessBox
            $tone={completeness.level === 'blocking' ? 'danger' : 'warning'}
            role={completeness.level === 'blocking' ? 'alert' : 'status'}
          >
            <CompletenessHead>
              <CompletenessTitle $tone={completeness.level === 'blocking' ? 'danger' : 'warning'}>
                {completenessLabel(completeness.level)}
              </CompletenessTitle>
            </CompletenessHead>
            <CompletenessExplain>{completenessExplanation(completeness)}</CompletenessExplain>
          </CompletenessBox>
        )}

        {ksPeriod && (
          <PeriodCheck
            $mismatch={showPeriodMismatch}
            role={showPeriodMismatch ? 'alert' : undefined}
          >
            <PeriodLabel>Период из документов</PeriodLabel>
            <PeriodValue>{fmtExtractedPeriod(ksPeriod)}</PeriodValue>
            {showPeriodMismatch && (
              <MismatchNote>
                Не совпадает с периодом обработки ({fmtPeriod(run.period)}) — проверьте выбранный
                месяц или состав документов.
              </MismatchNote>
            )}
          </PeriodCheck>
        )}

        {isRunActive(run.status) && (
          // Живой прогресс: подпись этапа + процент над полосой. Значения
          // приходят в реальном времени — RTK перетягивает карточку по
          // WS-событию `econ_run_updated` (см. rtkBridge), плюс редкий
          // страховочный poll на случай разрыва WS (см. setPoll ниже).
          <ProgressBlock
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(run.progress_percent)}
            aria-label={`Прогресс обработки: ${runStageLabel(run.status, run.progress_percent)}`}
          >
            <ProgressTopRow>
              <ProgressStage>{runStageLabel(run.status, run.progress_percent)}</ProgressStage>
              <ProgressPercent>{Math.round(run.progress_percent)}%</ProgressPercent>
            </ProgressTopRow>
            <Progress percent={run.progress_percent} />
          </ProgressBlock>
        )}

        {run.status === 'failed' && run.error_message && (
          <ErrBox style={{ marginTop: 16 }}>{run.error_message}</ErrBox>
        )}
        {actionError && <ErrBox style={{ marginTop: 16 }}>{actionError}</ErrBox>}

        {reportBuildError && (
          <ReportErrorBox role="alert">
            <ReportErrorTitle>Сборка отчёта не удалась</ReportErrorTitle>
            <ReportErrorReason>{reportBuildError}</ReportErrorReason>
            <ReportErrorHint>
              Исправьте причину и нажмите «Утвердить и собрать отчёт» повторно.
            </ReportErrorHint>
          </ReportErrorBox>
        )}

        {unmappedRevenue.length > 0 && (
          // role="status" (не "alert"): это предупреждение, не ошибка — расчёт
          // верен, баннер не должен перебивать чтение экрана. aria-live=polite
          // подразумевается ролью status.
          <UnmappedBox role="status">
            <UnmappedTitle>
              {unmappedRevenue.length === 1
                ? '1 договор не привязан к шаблону'
                : `${unmappedRevenue.length} договоров не привязаны к шаблону`}
            </UnmappedTitle>
            <UnmappedLead>
              По этим договорам выручка посчитана, но в карте активного шаблона нет строки для них —
              в собранном Excel по ним выйдет 0.
            </UnmappedLead>
            <UnmappedList>
              {unmappedRevenue.map((c) => (
                <UnmappedItem key={`${c.contract}-${c.metric}`}>
                  <UnmappedContract>
                    {c.contract}
                    {c.metric && <UnmappedMetric>{labelMetric(c.metric)}</UnmappedMetric>}
                  </UnmappedContract>
                  <UnmappedRevenue>{fmtRub(c.revenue)}</UnmappedRevenue>
                </UnmappedItem>
              ))}
            </UnmappedList>
            <UnmappedHint>
              Добавьте строки этих договоров в загруженный шаблон и пересоберите отчёт, чтобы их
              суммы попали в Excel.
            </UnmappedHint>
          </UnmappedBox>
        )}
      </Card>

      {/* HITL-ревью обработки: утвердить (→ сборка XLSX) или отклонить. */}
      {run.status === 'completed' && (
        <ReviewBar>
          {decided ? (
            <ReviewText>
              Решение по обработке:{' '}
              <Badge
                $tone={
                  run.decision === 'accepted'
                    ? 'success'
                    : run.decision === 'rejected'
                      ? 'neutral'
                      : 'warning'
                }
              >
                {run.decision === 'accepted'
                  ? 'утверждена'
                  : run.decision === 'rejected'
                    ? 'отклонена'
                    : 'спорно'}
              </Badge>
            </ReviewText>
          ) : approval.allowed ? (
            <ReviewText>Проверьте показатели и аномалии, затем утвердите обработку.</ReviewText>
          ) : (
            <ReviewText>
              Проверьте показатели и разберите аномалии — обработку утверждает главный экономист
              или руководитель.
            </ReviewText>
          )}
          {!decided && (
            <HeadActions>
              <Button
                type="button"
                $variant="ghost"
                $size="sm"
                onClick={() => handleDecision('rejected')}
                disabled={decideHint !== undefined || deciding}
                title={decideHint}
              >
                Отклонить
              </Button>
              <ActionGroup>
                <Button
                  type="button"
                  $variant="primary"
                  $size="sm"
                  onClick={() => handleDecision('accepted')}
                  disabled={approveDisabled}
                  title={approveHint}
                  aria-describedby={approveHint ? 'approve-hint' : undefined}
                >
                  {deciding ? 'Сохраняю…' : 'Утвердить и собрать отчёт'}
                </Button>
                {approveHint && <ActionHint id="approve-hint">{approveHint}</ActionHint>}
              </ActionGroup>
            </HeadActions>
          )}
        </ReviewBar>
      )}

      {/* Блок 1 — Результат: готовые показатели, сгруппированные по договору. */}
      <Card>
        <SectionTitle>
          Результат <Badge $tone="neutral">{data.facts.length}</Badge>
        </SectionTitle>
        <FactsResult
          facts={data.facts}
          sources={data.sources}
          revenueIncomplete={revenueLikelyIncomplete(completeness.level)}
        />
      </Card>

      {/* Комментарии к отклонениям план/факт (Р-13) — если backend их сгенерировал. */}
      {deviationCommentList.length > 0 && (
        <Card>
          <SectionTitle>
            Комментарии к отклонениям{' '}
            <Badge $tone="neutral">{deviationCommentList.length}</Badge>
          </SectionTitle>
          <DeviationComments comments={deviationCommentList} />
        </Card>
      )}

      {/* Платёжный календарь — только для модуля ДДС (М4). */}
      {run.module === 'cashflow' && (
        <Card>
          <SectionTitle>Платёжный календарь</SectionTitle>
          <CashCalendar runId={run.id} />
        </Card>
      )}

      {/* Пакет руководству — консолидированный KPI-пакет (М5). */}
      {run.module === 'exec_pack' && (
        <Card>
          <SectionTitle>Пакет руководству</SectionTitle>
          <ExecPackCard run={run} />
        </Card>
      )}

      {/* Блок 2 — Аномалии: что требует внимания. */}
      <Card>
        <SectionTitle>
          Аномалии <Badge $tone="neutral">{data.anomalies.length}</Badge>
        </SectionTitle>
        <AnomaliesPanel
          anomalies={data.anomalies}
          runId={run.id}
          objectScope={run.object_scope}
        />
      </Card>

      {/* Блок 3 — Источники (трассировка): свёрнут по умолчанию. */}
      <SourcesSection
        rows={data.rows}
        facts={data.facts}
        sources={data.sources}
        runId={run.id}
        canWrite={can_write}
      />

      <Card>
        <SectionTitle>Спросить агента</SectionTitle>
        <RunChatPanel runId={run.id} />
      </Card>

      <ConfirmDialog
        open={confirmOpen}
        title="Удалить обработку?"
        warning="Обработка, извлечённые строки, расчётные показатели и аномалии будут удалены безвозвратно. Это действие нельзя отменить."
        confirmText={fmtPeriod(run.period)}
        confirmLabel={`Введите период для подтверждения: ${fmtPeriod(run.period)}`}
        confirmButtonLabel="Удалить обработку"
        busyLabel="Удаление…"
        isBusy={deleting}
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </Wrap>
  );
}
